/**
 * Persist a provider calendar appointment to Medplum.
 *
 * Called from ProviderCalendarPage in live (non-mock) mode whenever the
 * provider creates, edits, or cancels an appointment.
 *
 * Extension URLs mirror medplum-server/src/fhir-model/constants.ts — keep in
 * sync if those canonical URLs change.
 */

import type { MedplumClient } from '@medplum/core';
import type { Appointment, AppointmentParticipant, CodeableConcept, Extension, Meta, Slot } from '@medplum/fhirtypes';
import {
  APPOINTMENT_CATEGORY_SYSTEM as SERVER_APPT_CATEGORY_SYSTEM,
  APPOINTMENT_GROUP_EXT,
  CALENDAR_EVENT_CATEGORY_SYSTEM,
  MEETING_TYPE_SYSTEM,
  TIME_ENTRY_TYPE_SYSTEM,
  VIDEO_MEETING_URL_EXT,
  RECURRING_PATTERN_EXT,
  EPISODE_OF_CARE_CONTEXT_EXT,
} from '@unify/fhir-model/constants';
import {
  appointmentStartUnchanged,
  declineClientParticipants,
  mergeAppointmentParticipants,
} from '@unify/fhir-model/resources/appointment';
import { getPatientAccountRefs, mergeCompartmentAccountRefs } from '../../lib/clientFacingTasks';
import { toFhirInstant } from '../../lib/dateHelpers';
import { updateAppointmentFresh } from '../../lib/telehealth/updateAppointmentFresh';

/**
 * Overlay form-owned extensions onto an existing Appointment.extension list.
 * Form URLs replace; all other existing extensions (e.g. bot-written session fields) are kept.
 * VIDEO_MEETING_URL is only form-owned when present in `formExtensions` (videoLink provided).
 * EPISODE_OF_CARE_CONTEXT and RECURRING_PATTERN are always form-owned (omit clears).
 */
export function mergeCalendarAppointmentExtensions(
  existing: Extension[] | undefined,
  formExtensions: Extension[],
): Extension[] {
  const writtenUrls = new Set(formExtensions.map((ext) => ext.url));
  // Always form-owned: replace when provided, clear when omitted.
  writtenUrls.add(RECURRING_PATTERN_EXT);
  writtenUrls.add(EPISODE_OF_CARE_CONTEXT_EXT);
  const kept = (existing ?? []).filter((ext) => !writtenUrls.has(ext.url));
  return [...kept, ...formExtensions];
}

const PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';
const ROOM_PARTICIPANT_TYPE_SYSTEM = 'https://unify.health/fhir/CodeSystem/participant-type';
const APPOINTMENT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0276';
const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';

export const APPOINTMENT_CATEGORY_SYSTEM = SERVER_APPT_CATEGORY_SYSTEM;
export { MEETING_TYPE_SYSTEM, TIME_ENTRY_TYPE_SYSTEM };
export type AppointmentCategory = 'session' | 'group' | 'meeting' | 'time-entry';

function parseResourceIdFromReference(
  reference: string | undefined,
  resourceType: string,
): string | undefined {
  if (!reference) return undefined;
  const marker = `${resourceType}/`;
  const idx = reference.lastIndexOf(marker);
  if (idx < 0) return undefined;
  const candidate = reference.slice(idx + marker.length).split(/[/?#]/)[0];
  return candidate || undefined;
}

function participantHasType(
  participant: AppointmentParticipant,
  system: string,
  code: string,
): boolean {
  return (
    participant.type?.some((type) =>
      type.coding?.some((coding) => coding.system === system && coding.code === code),
    ) ?? false
  );
}

function getScheduledActorReferences(appointment: Appointment): string[] {
  const refs = new Set<string>();
  for (const participant of appointment.participant ?? []) {
    const reference = participant.actor?.reference;
    if (!reference) continue;
    if (
      reference.startsWith('Location/')
      && participantHasType(participant, ROOM_PARTICIPANT_TYPE_SYSTEM, 'ROOM')
    ) {
      refs.add(reference);
    }
    if (
      reference.startsWith('Device/')
      && participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'DEV')
    ) {
      refs.add(reference);
    }
  }
  return [...refs];
}

/** Calendar UI status string → FHIR Appointment status. Outcome statuses wait for note sign. */
function toFhirStatus(calendarStatus: string): Appointment['status'] {
  if (calendarStatus === 'proposed') return 'proposed';
  return 'booked';
}

function buildAppointmentType(modality: string): Appointment['appointmentType'] {
  if (modality === 'telehealth') {
    return {
      coding: [{ system: APPOINTMENT_TYPE_SYSTEM, code: 'VR', display: 'Telehealth' }],
      text: 'Telehealth',
    };
  }
  if (modality === 'hybrid') {
    return {
      coding: [{ system: APPOINTMENT_TYPE_SYSTEM, code: 'VR', display: 'Hybrid' }],
      text: 'Hybrid',
    };
  }
  return {
    coding: [{ system: APPOINTMENT_TYPE_SYSTEM, code: 'ROUTINE', display: 'In-person' }],
    text: 'In-person',
  };
}

function buildServiceTypes(params: {
  cptCode?: string;
  cptDisplay?: string;
  serviceTypeCode?: string;
  serviceTypeDisplay?: string;
  serviceTypeSystem?: string;
  sessionHealthcareServiceId?: string;
  sessionHealthcareServiceDisplay?: string;
}): CodeableConcept[] | undefined {
  const {
    cptCode,
    cptDisplay,
    serviceTypeCode,
    serviceTypeDisplay,
    serviceTypeSystem,
    sessionHealthcareServiceId,
    sessionHealthcareServiceDisplay,
  } = params;
  const result: CodeableConcept[] = [];
  if (sessionHealthcareServiceId) {
    result.push({
      coding: [{
        system: CALENDAR_EVENT_CATEGORY_SYSTEM,
        code: sessionHealthcareServiceId,
        display: sessionHealthcareServiceDisplay,
      }],
      ...(sessionHealthcareServiceDisplay ? { text: sessionHealthcareServiceDisplay } : {}),
    });
  }
  if (serviceTypeCode && serviceTypeSystem) {
    result.push({
      coding: [{ system: serviceTypeSystem, code: serviceTypeCode, display: serviceTypeDisplay }],
      ...(serviceTypeDisplay ? { text: serviceTypeDisplay } : {}),
    });
  }
  if (cptCode) {
    result.push({
      coding: [{ system: CPT_SYSTEM, code: cptCode, display: cptDisplay }],
      ...(cptDisplay ? { text: cptDisplay } : {}),
    });
  }
  return result.length > 0 ? result : undefined;
}

async function buildAppointmentMeta(
  medplum: Pick<MedplumClient, 'readResource'>,
  patientId: string | undefined,
  organizationId: string | undefined,
): Promise<Meta | undefined> {
  let meta: Meta | undefined;

  if (patientId) {
    const careTeamAccounts = await getPatientAccountRefs(medplum as MedplumClient, patientId);
    meta = {
      account: { reference: `Patient/${patientId}` },
      ...(careTeamAccounts.length > 0 ? { accounts: careTeamAccounts } : {}),
    };
  }

  if (organizationId?.trim()) {
    const orgRef = { reference: `Organization/${organizationId.trim()}` };
    meta = {
      ...(meta ?? {}),
      accounts: mergeCompartmentAccountRefs(meta?.accounts ?? [], [orgRef]),
    };
  }

  return meta;
}

export interface SaveCalendarAppointmentArgs {
  medplum: Pick<MedplumClient, 'createResource' | 'updateResource' | 'readResource' | 'deleteResource'>;
  /**
   * When set, the existing Appointment is updated (PUT) instead of created.
   * Only pass FHIR-issued IDs (not local temp IDs).
   */
  appointmentId?: string;
  start: Date;
  end: Date;
  /** Calendar UI status value (e.g. 'scheduled', 'proposed', 'attended'). */
  calendarStatus: string;
  /** Performing provider's Practitioner.id (no 'Practitioner/' prefix) — the
   * selected session provider, not necessarily the logged-in user. */
  practitionerId: string;
  practitionerDisplay?: string;
  /** Practice site Location.id for LOC participant. */
  siteId?: string;
  /** Practice Organization.id — required for org-scoped roles (practice owner/admin). */
  organizationId?: string;
  /** Patient.id (no 'Patient/' prefix). Omit for non-billable events. */
  patientId?: string;
  patientDisplay?: string;
  /** EpisodeOfCare.id (no prefix). Links the appointment to a care episode. */
  episodeOfCareId?: string;
  /** CPT code, e.g. '90837'. Used for billable sessions. */
  cptCode?: string;
  cptDisplay?: string;
  /**
   * Non-CPT service type (meeting type, time-entry type).
   * When set, stored in `serviceType` with the given system instead of CPT.
   */
  serviceTypeCode?: string;
  serviceTypeDisplay?: string;
  serviceTypeSystem?: string;
  /** HealthcareService id for clinical session/group appointments (catalog round-trip). */
  sessionHealthcareServiceId?: string;
  /** HealthcareService.name shown as the appointment's Session Type. */
  sessionHealthcareServiceDisplay?: string;
  /**
   * Calendar sub-category. Stored in `serviceCategory` so the mapper can
   * restore the correct form tab when the appointment is loaded back.
   */
  appointmentCategory?: AppointmentCategory;
  modality?: string;
  description?: string;
  videoLink?: string;
  /** Recurring pattern code ('daily'|'weekly'|'biweekly'|'monthly'|'none'). */
  recurringPattern?: string;
  /** Additional participants beyond the primary practitioner and patient
   * (meeting/group attendees, or RelatedPerson session participants). */
  additionalParticipants?: Array<{
    resourceType: 'Practitioner' | 'Patient' | 'RelatedPerson';
    id: string;
    display?: string;
  }>;
  /** Therapy cohort Group.id (no prefix). Links one-off group sessions to FHIR Group. */
  therapyGroupId?: string;
  therapyGroupDisplay?: string;
  /**
   * Keep Patient/Practitioner participants this form does not render — the cohort
   * roster and co-facilitators of a group session, which the cohort owns.
   */
  preserveExistingParticipants?: boolean;
}

/**
 * Build and persist a FHIR Appointment to Medplum.
 * Creates a new resource when `appointmentId` is absent; updates otherwise.
 */
export async function saveCalendarAppointment(
  args: SaveCalendarAppointmentArgs,
): Promise<Appointment> {
  const {
    medplum,
    appointmentId,
    start,
    end,
    calendarStatus,
    practitionerId,
    practitionerDisplay,
    siteId,
    organizationId,
    patientId,
    patientDisplay,
    episodeOfCareId,
    cptCode,
    cptDisplay,
    serviceTypeCode,
    serviceTypeDisplay,
    serviceTypeSystem,
    sessionHealthcareServiceId,
    sessionHealthcareServiceDisplay,
    appointmentCategory,
    modality = 'in-person',
    description,
    videoLink,
    recurringPattern,
    additionalParticipants,
    therapyGroupId,
    therapyGroupDisplay,
    preserveExistingParticipants = false,
  } = args;

  const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  const resolvedServiceTypes = buildServiceTypes({
    cptCode,
    cptDisplay,
    serviceTypeCode,
    serviceTypeDisplay,
    serviceTypeSystem,
    sessionHealthcareServiceId,
    sessionHealthcareServiceDisplay,
  });

  const participants: NonNullable<Appointment['participant']> = [
    {
      actor: {
        reference: `Practitioner/${practitionerId}`,
        ...(practitionerDisplay ? { display: practitionerDisplay } : {}),
      },
      type: [
        {
          coding: [
            { system: PARTICIPANT_TYPE_SYSTEM, code: 'PPRF', display: 'Primary Performer' },
          ],
        },
      ],
      status: 'accepted',
    },
  ];

  if (patientId) {
    participants.push({
      actor: {
        reference: `Patient/${patientId}`,
        ...(patientDisplay ? { display: patientDisplay } : {}),
      },
      type: [
        {
          coding: [{ system: PARTICIPANT_TYPE_SYSTEM, code: 'SBJ', display: 'Subject' }],
        },
      ],
      status: 'accepted',
    });
  }

  if (siteId) {
    participants.push({
      actor: {
        reference: `Location/${siteId}`,
      },
      type: [
        {
          coding: [{ system: PARTICIPANT_TYPE_SYSTEM, code: 'LOC', display: 'Location' }],
        },
      ],
      status: 'accepted',
    });
  }

  if (additionalParticipants) {
    for (const p of additionalParticipants) {
      const alreadyPresent = participants.some(
        (existing) => existing.actor?.reference === `${p.resourceType}/${p.id}`,
      );
      if (alreadyPresent) continue;
      /** RelatedPerson episode participants are PART per the appointments PRD;
       * other extras (meeting/group attendees) stay ATND. */
      const typeCoding = p.resourceType === 'RelatedPerson'
        ? { system: PARTICIPANT_TYPE_SYSTEM, code: 'PART', display: 'Participation' }
        : { system: PARTICIPANT_TYPE_SYSTEM, code: 'ATND', display: 'Attender' };
      participants.push({
        actor: {
          reference: `${p.resourceType}/${p.id}`,
          ...(p.display ? { display: p.display } : {}),
        },
        type: [{ coding: [typeCoding] }],
        status: 'accepted',
      });
    }
  }

  const participantsForWrite =
    calendarStatus === 'clinician-cancelled'
      ? declineClientParticipants(participants, 'clinician-cancelled')
      : participants;

  const extensions: NonNullable<Appointment['extension']> = [];
  if (episodeOfCareId) {
    extensions.push({
      url: EPISODE_OF_CARE_CONTEXT_EXT,
      valueReference: { reference: `EpisodeOfCare/${episodeOfCareId}` },
    });
  }
  if (videoLink?.trim()) {
    extensions.push({ url: VIDEO_MEETING_URL_EXT, valueUrl: videoLink.trim() });
  }
  if (recurringPattern && recurringPattern !== 'none') {
    extensions.push({ url: RECURRING_PATTERN_EXT, valueCode: recurringPattern });
  }
  if (therapyGroupId) {
    extensions.push({
      url: APPOINTMENT_GROUP_EXT,
      valueReference: {
        reference: `Group/${therapyGroupId}`,
        ...(therapyGroupDisplay ? { display: therapyGroupDisplay } : {}),
      },
    });
  }

  const groupSupportingInfo = therapyGroupId
    ? [
        {
          reference: `Group/${therapyGroupId}`,
          ...(therapyGroupDisplay ? { display: therapyGroupDisplay } : {}),
        },
      ]
    : undefined;

  const resource: Appointment = {
    resourceType: 'Appointment',
    ...(appointmentId ? { id: appointmentId } : {}),
    status: toFhirStatus(calendarStatus),
    cancelationReason: undefined,
    start: toFhirInstant(start),
    end: toFhirInstant(end),
    minutesDuration: durationMinutes,
    ...(description?.trim() ? { description: description.trim() } : {}),
    // serviceCategory stores the calendar sub-type so the UI can restore the
    // correct form tab (session/group/meeting/time-entry) on edit.
    ...(appointmentCategory
      ? {
        serviceCategory: [
          {
            coding: [{ system: APPOINTMENT_CATEGORY_SYSTEM, code: appointmentCategory }],
            text: appointmentCategory,
          },
        ],
      }
      : {}),
    ...(resolvedServiceTypes ? { serviceType: resolvedServiceTypes } : {}),
    appointmentType: buildAppointmentType(modality),
    participant: participantsForWrite,
    ...(groupSupportingInfo ? { supportingInformation: groupSupportingInfo } : {}),
    ...(extensions.length > 0 ? { extension: extensions } : {}),
  };

  const appointmentMeta = await buildAppointmentMeta(medplum, patientId, organizationId);
  /** A group session has no single subject patient, so recomputing meta here would
   * drop the account compartment it was created with. */
  if (appointmentMeta && !(preserveExistingParticipants && !patientId)) {
    resource.meta = appointmentMeta;
  }

  if (appointmentId) {
    // Fresh read + If-Match (re-applied on a 412): only the calendar-owned fields below are written,
    // everything else (e.g. the bot-owned chime-session extension) comes from the latest version.
    return updateAppointmentFresh(medplum, appointmentId, (existing) => {
      const mergedExtensions = mergeCalendarAppointmentExtensions(existing.extension, extensions);
      // Presence periods belong to the old session: keep them unless the start moves (an end-only change resizes it).
      const keepPeriods = appointmentStartUnchanged(existing, resource.start);
      const mergedParticipants =
        calendarStatus === 'clinician-cancelled'
          ? declineClientParticipants(
            mergeAppointmentParticipants(existing.participant, participantsForWrite, {
              keepUnmatchedPatients: preserveExistingParticipants,
              keepPeriods,
            }),
            'clinician-cancelled',
          )
          : mergeAppointmentParticipants(existing.participant, participantsForWrite, {
            keepUnmatchedPatients: preserveExistingParticipants,
            keepPeriods,
          });
      // Preserve non-EoC / non-Group supporting refs; apply form Group link when present;
      // re-attach EpisodeOfCare when the form still has an episode context.
      const otherSupporting = (existing.supportingInformation ?? []).filter(
        (ref) =>
          !ref.reference?.startsWith('EpisodeOfCare/')
          && !ref.reference?.startsWith('Group/'),
      );
      const groupRefs =
        groupSupportingInfo
        ?? (existing.supportingInformation ?? []).filter((r) => r.reference?.startsWith('Group/'));
      const supportingInformation = [
        ...otherSupporting,
        ...groupRefs,
        ...(episodeOfCareId ? [{ reference: `EpisodeOfCare/${episodeOfCareId}` }] : []),
      ];
      // Outcome status (fulfilled/noshow/cancelled) and its reason are derived from attendance;
      // the only transition this writer owns is confirming a proposed hold.
      const confirmsHold =
        existing.status === 'proposed'
        && (calendarStatus === 'scheduled' || calendarStatus === 'confirmed');
      return {
        ...existing,
        ...resource,
        status: confirmsHold ? 'booked' : existing.status,
        cancelationReason: existing.cancelationReason,
        participant: mergedParticipants,
        ...(mergedExtensions.length > 0 ? { extension: mergedExtensions } : { extension: undefined }),
        ...(supportingInformation.length > 0
          ? { supportingInformation }
          : { supportingInformation: undefined }),
      };
    });
  }

  return medplum.createResource(resource);
}

/**
 * Drag/resize write: sets only start/end/duration (and drops participant presence periods when the start moves) on a fresh read (If-Match, re-applied on a 412),
 * so bot-owned state such as `chime-session` is never copied from a stale read.
 */
export function moveCalendarAppointment(
  medplum: Pick<MedplumClient, 'readResource' | 'updateResource'>,
  appointmentId: string,
  newStart: Date,
  newEnd: Date,
): Promise<Appointment> {
  return updateAppointmentFresh(medplum, appointmentId, (fresh) => {
    const start = newStart.toISOString();
    const end = newEnd.toISOString();
    return {
      ...fresh,
      start,
      end,
      minutesDuration: Math.round((newEnd.getTime() - newStart.getTime()) / 60_000),
      // Presence periods belong to the old session: a drag that moves the start drops them; a resize
      // that keeps the start keeps them.
      ...(appointmentStartUnchanged(fresh, start) || !fresh.participant?.some((p) => p.period)
        ? {}
        : {
          participant: fresh.participant.map((p) => {
            const withoutPeriod = { ...p };
            delete withoutPeriod.period;
            return withoutPeriod;
          }),
        }),
    };
  });
}

/**
 * Delete an existing Medplum appointment.
 */
export async function deleteCalendarAppointment(
  medplum: Pick<MedplumClient, 'readResource' | 'deleteResource' | 'invalidateSearches' | 'searchResources'>,
  appointmentId: string,
): Promise<void> {
  const appointment = await medplum.readResource('Appointment', appointmentId);
  const slotIds = new Set(
    (appointment.slot ?? [])
      .map((slotRef) => parseResourceIdFromReference(slotRef.reference, 'Slot'))
      .filter((slotId): slotId is string => Boolean(slotId)),
  );

  if (appointment.start && appointment.end) {
    const actorRefs = getScheduledActorReferences(appointment);
    for (const actorRef of actorRefs) {
      const schedules = await medplum.searchResources('Schedule', {
        actor: actorRef,
        _count: '10',
      });
      for (const schedule of schedules) {
        if (!schedule.id) continue;
        const slots: Slot[] = await medplum.searchResources('Slot', {
          schedule: `Schedule/${schedule.id}`,
          status: 'busy',
          start: appointment.start,
          _count: '50',
        });
        for (const slot of slots) {
          if (slot.id && slot.start === appointment.start && slot.end === appointment.end) {
            slotIds.add(slot.id);
          }
        }
      }
    }
  }

  for (const slotId of slotIds) {
    await medplum.deleteResource('Slot', slotId);
  }
  await medplum.deleteResource('Appointment', appointmentId);
  medplum.invalidateSearches('Appointment');
  medplum.invalidateSearches('Slot');
  medplum.invalidateSearches('Schedule');
}

/**
 * Returns true if the given event ID was issued by Medplum (not a local
 * temp ID generated by `Date.now().toString()`).
 */
export function isMedplumAppointmentId(id: string): boolean {
  return !/^\d+$/.test(id);
}
